import PageState from "shared/util/PageState";
import _str from "shared/util/_str";
import UrlHelpers from "shared/util/UrlHelpers";
import isApp from "shared/util/isApp";

import * as web from "apps/questions/util/web";
import { QUESTIONS_PAGE_STATE_KEY } from "apps/questions/util/constants";
import { getSelfviewQuestionCategories } from "apps/profile_questions/util/helpers";

import {
	USER_TYPES,
	ANSWERED_ALL_QUESTIONS_KEY,
} from "apps/profile_questions/util/constants";

import { areAllAccepted } from "apps/questions/util/helpers";

import {
	CLEAR_ANSWERING_QUESTION,
	GET_NEXT_QUESTION,
	GET_NEXT_QUESTION_SUCCESS,
	GET_NEXT_QUESTION_FAILURE,
	getQuestions,
	getFilters,
	getUserInfo,
	setAnsweringQuestion,
} from "ducks/profile_questions";


// Action types
// ----------------------------------------------------------------------------

export const FETCH_BINARY_QUESTION = "QUESTIONS.FETCH_BINARY_QUESTION";
export const FETCH_BINARY_QUESTION_SUCCESS = "QUESTIONS.FETCH_BINARY_QUESTION_SUCCESS";
export const FETCH_BINARY_QUESTION_FAILURE = "QUESTIONS.FETCH_BINARY_QUESTION_FAILURE";

export const SUBMIT_ANSWER = "QUESTIONS.SUBMIT_ANSWER";
export const SUBMIT_ANSWER_SUCCESS = "QUESTIONS.SUBMIT_ANSWER_SUCCESS";
export const SUBMIT_ANSWER_FAILURE = "QUESTIONS.SUBMIT_ANSWER_FAILURE";

export const SKIP_ANSWER = "QUESTIONS.SKIP_ANSWER";
export const SKIP_ANSWER_SUCCESS = "QUESTIONS.SKIP_ANSWER_SUCCESS";
export const SKIP_ANSWER_FAILURE = "QUESTIONS.SKIP_ANSWER_FAILURE";

export const FETCH_QUESTION = "QUESTIONS.FETCH_QUESTION";
export const FETCH_QUESTION_SUCCESS = "QUESTIONS.FETCH_QUESTION_SUCCESS";
export const FETCH_QUESTION_FAILURE = "QUESTIONS.FETCH_QUESTION_FAILURE";

export const GET_NEXT_BINARY_QUESTION = "QUESTIONS.GET_NEXT_BINARY_QUESTION";
export const SUBMIT_BINARY_ANSWER = "QUESTIONS.SUBMIT_BINARY_ANSWER";
export const SUBMIT_BINARY_ANSWER_SUCCESS = "QUESTIONS.SUBMIT_BINARY_ANSWER_SUCCESS";
export const SUBMIT_BINARY_ANSWER_FAILURE = "QUESTIONS.SUBMIT_BINARY_ANSWER_FAILURE";

export const SELECT_ANSWER = "QUESTIONS.SELECT_ANSWER";
export const TOGGLE_ACCEPTED_ANSWER = "QUESTIONS.TOGGLE_ACCEPTED_ANSWER";
export const SET_IMPORTANCE = "QUESTIONS.SET_IMPORTANCE";
export const TOGGLE_PRIVACY = "QUESTIONS.TOGGLE_PRIVACY";
export const CHANGE_EXPLANATION = "QUESTIONS.CHANGE_EXPLANATION";

export const SET_GENRE = "QUESTIONS.SET_GENRE";
export const DISPLAYED_QUESTION = "QUESTIONS.DISPLAYED_QUESTION";
export const SET_BINARY_QUESTIONS_MODE = "QUESTIONS.SET_BINARY_QUESTIONS_MODE";


// Initial state
// ----------------------------------------------------------------------------

export const INITIAL_STATE = {
	question: {
		id: 0,
		genre: "",
		question: "",
		answers: [
			{
				text: "",
				isSelected: false,
				isAccepted: false,
			},
			{
				text: "",
				isSelected: false,
				isAccepted: false,
			},
		],
		importance: null,
		lastImportance: null,
		isPrivate: false,
		isSkippable: false,
		explanation: "",
		wasAnswered: false,
	},

	answeredQuestions: {},
	answeredQuestionsOrder: [],

	error: null,
	genres: [],
	currentGenre: null,

	isLoading: false,
	isProcessing: false,
	isAtEnd: false,
	isBinaryMode: false,
};

let initialState;
let lastPageState;

if (
	process.env.NODE_ENV !== "test" &&
	PageState &&
	typeof PageState.get === "function"
) {
	lastPageState = PageState.get(QUESTIONS_PAGE_STATE_KEY);
}

if (lastPageState) {
	initialState = {
		...INITIAL_STATE,
		...lastPageState,
	};

	PageState.set({ [QUESTIONS_PAGE_STATE_KEY]: null });
} else if (typeof window !== "undefined" && window.QuestionsParams) {
	initialState = {
		...INITIAL_STATE,
		...window.QuestionsParams,
	};
} else {
	initialState = INITIAL_STATE;
}


// Reducer
// ----------------------------------------------------------------------------

export function reducer(state = initialState, action) {
	switch (action.type) {
		case FETCH_BINARY_QUESTION: {
			return state;
		}

		case FETCH_BINARY_QUESTION_SUCCESS: {
			return {
				...state,
				...action.payload,
			};
		}

		case FETCH_BINARY_QUESTION_FAILURE: {
			return {
				...state,
				...action.payload,
			};
		}

		case SELECT_ANSWER: {
			const { index } = action.payload;

			return {
				...state,
				question: {
					...state.question,
					answers: state.question.answers.map((answer, i) => ({
						...answer,
						isSelected: index === i,
					})),
				},
			};
		}

		case TOGGLE_ACCEPTED_ANSWER: {
			const { index } = action.payload;
			const { answers } = state.question;
			const newAnswers = answers.map((answer, i) => ({
				...answer,
				isAccepted: index === i ? !answer.isAccepted : answer.isAccepted,
			}));

			const lastStateWasIrrelevant = areAllAccepted(answers);
			const thisStateIsIrrelevant = areAllAccepted(newAnswers);
			let importance = state.question.importance;

			if (
				lastStateWasIrrelevant &&
				!thisStateIsIrrelevant &&
				state.question.lastImportance
			) {
				importance = state.question.lastImportance;
			}

			return {
				...state,
				question: {
					...state.question,
					answers: newAnswers,
					importance,
				},
			};
		}

		case SET_IMPORTANCE: {
			return {
				...state,
				question: {
					...state.question,
					lastImportance: state.question.importance,
					importance: action.payload.importance,
				},
			};
		}

		case TOGGLE_PRIVACY: {
			return {
				...state,
				question: {
					...state.question,
					isPrivate: !state.question.isPrivate,
				},
			};
		}

		case CHANGE_EXPLANATION: {
			const { explanation } = action.payload;

			return {
				...state,
				question: {
					...state.question,
					explanation,
				},
			};
		}

		// SUBMISSIONS
		case SUBMIT_ANSWER: {
			return {
				...state,
				isProcessing: true,
			};
		}

		case SUBMIT_ANSWER_FAILURE: {
			return {
				...state,
				isProcessing: false,
				error: action.payload.error,
			};
		}

		case SUBMIT_BINARY_ANSWER: {
			return {
				...state,
				isProcessing: true,
			};
		}

		case SUBMIT_BINARY_ANSWER_SUCCESS: {
			return {
				...state,
				isProcessing: false,
			};
		}

		case SUBMIT_BINARY_ANSWER_FAILURE: {
			return {
				...state,
				isProcessing: false,
				error: action.payload.error,
			};
		}

		case SKIP_ANSWER: {
			return {
				...state,
				isProcessing: true,
			};
		}

		case SKIP_ANSWER_SUCCESS: {
			return {
				...state,
				isProcessing: false,
			};
		}

		case FETCH_QUESTION: {
			return {
				...state,
				isLoading: true,
				error: null,
			};
		}

		case FETCH_QUESTION_SUCCESS: {
			return {
				...state,
				isLoading: false,
				isProcessing: false,
				isAtEnd: false,
				question: action.payload.question,
			};
		}

		case FETCH_QUESTION_FAILURE: {
			return {
				...state,
				isLoading: false,
				isProcessing: false,
				error: action.payload.error,
				isAtEnd: action.payload.isAtEnd,
			};
		}

		case GET_NEXT_BINARY_QUESTION: {
			const binaryQuestions = state.binary_questions.splice(1);

			return {
				...state,
				binary_questions: binaryQuestions,
				question: binaryQuestions[0],
			};
		}

		case SET_GENRE: {
			return {
				...state,
				currentGenre: action.payload.genre,
			};
		}

		case SET_BINARY_QUESTIONS_MODE: {
			return {
				...state,
				isBinaryMode: action.payload.isBinaryMode,
			};
		}

		default: {
			return state;
		}
	}
}


// Action creators
// ----------------------------------------------------------------------------

export const fetchBinaryQuestion = () => (dispatch) => {
	dispatch({
		type: FETCH_BINARY_QUESTION,
	});

	return web.fetchBinaryQuestion()
		.then((question) => {
			dispatch({
				type: FETCH_BINARY_QUESTION_SUCCESS,
				payload: question,
			});
		})
		.catch((error) => {
			dispatch({
				type: FETCH_BINARY_QUESTION_FAILURE,
				payload: error,
			});
		});
};

export function displayQuestion() {
	return { type: DISPLAYED_QUESTION };
}

export function fetchQuestion(qid) {
	return (dispatch) => {
		dispatch({ type: FETCH_QUESTION });

		web.fetchQuestion(qid)
			.then((res) => {
				dispatch({
					type: FETCH_QUESTION_SUCCESS,
					payload: {
						question: res,
					},
				});
			})
			.catch((e) => {
				dispatch({
					type: FETCH_QUESTION_FAILURE,
					payload: {
						error: e.error.message,
						isAtEnd: e.error.message === "No more questions available",
					},
				});
			});
	};
}

export function fetchNextQuestion() {
	const genericError = _str("There was a problem getting your next question!");

	return (dispatch, getStore) => {
		const store = getStore();
		const isBinaryMode = store.questions.isBinaryMode;
		const binaryQuestions = store.questions.binary_questions;

		// if we have more than one binary question, get the next one;
		// otherwise, get the next non-binary question
		if (isBinaryMode && binaryQuestions && binaryQuestions.length > 1) {
			dispatch({ type: GET_NEXT_BINARY_QUESTION });
			return;
		}

		dispatch({ type: GET_NEXT_QUESTION });

		web.fetchNextQuestion()
			.then((res) => {
				dispatch({
					type: FETCH_QUESTION_SUCCESS,
					payload: {
						question: res,
					},
				});

				dispatch({
					type: GET_NEXT_QUESTION_SUCCESS,
					payload: {
						question: res,
					},
				});

				// update the profile questions app with the next question id
				dispatch(setAnsweringQuestion(res.id));
			})
			.catch((e) => {
				// catch the no more questions "error" to show a completion state
				if (
					typeof e === "object" &&
					e.error && e.error.type && e.error.type === "NoResults"
				) {
					dispatch({
						type: GET_NEXT_QUESTION_SUCCESS,
						payload: {
							question: {
								[ANSWERED_ALL_QUESTIONS_KEY]: true,
							},
						},
					});

					dispatch({
						type: FETCH_QUESTION_FAILURE,
						error: true,
						payload: {
							error: null,
							isAtEnd: true,
						},
					});
				} else {
					console.error(e);

					const error = e && e.error ? e : { error: genericError };

					dispatch({
						type: FETCH_QUESTION_FAILURE,
						error: true,
						payload: {
							error: error.message,
						},
					});

					dispatch({
						type: GET_NEXT_QUESTION_FAILURE,
						error: true,
						payload: {
							error: error.message,
						},
					});
				}
			});
	};
}

export function submitQuestion() {
	const defaultError = _str("There was a problem saving your answer!");

	return (dispatch, getStore) => {
		const store = getStore().questions;
		const profileQuestionsStore = getStore().profileQuestions;
		const { question, isProcessing } = store;

		// if we're already fetching, do nothing
		if (isProcessing) {
			return;
		}

		dispatch({ type: SUBMIT_ANSWER });

		let targetUserid;
		const tuidParam = UrlHelpers.getParam("tuid");

		if (profileQuestionsStore && profileQuestionsStore.isSelfView) {
			targetUserid = profileQuestionsStore[USER_TYPES.VIEWER].userid;
		} else if (profileQuestionsStore && !profileQuestionsStore.isSelfView) {
			targetUserid = profileQuestionsStore[USER_TYPES.TARGET].userid;
		} else if (tuidParam) {
			// if we don't have the profile questions store, check the URL for `tuid`;
			// that's how the mobile apps indicate a target user.
			targetUserid = tuidParam;
		}

		web.answerQuestion(question, targetUserid)
			.then((res) => {
				if (res.success) {
					dispatch({
						type: SUBMIT_ANSWER_SUCCESS,
						payload: {
							num_questions: res.num_questions,
							question: res.question,
							profileQuestionsFilterId: profileQuestionsStore ?
								profileQuestionsStore.activeFilter : null,
							profileQuestionsFilterName: profileQuestionsStore && profileQuestionsStore.filters ?
								(
									profileQuestionsStore.filters.find((item) => {
										return item.filter_id === profileQuestionsStore.activeFilter;
									}) || {}
								).title :
								null,
						},
					});

					if (res.num_questions && window.QuestionMilestones) {
						window.QuestionMilestones.updateCount(res.num_questions);
					}

					// if we see the profile questions app, tell it to take additional
					// action(s):
					// * if we're in self view and answering the next question, we want
					//   to load the next question in this answer modal. and in the
					//   background, we refetch the questions list to get updated paging
					//   (if the question is in a visible category).
					// * if we're on public view or self-view and reanswering a question,
					//   we close this modal, trigger a fetch to update the profile
					//   questions app's filters list, and reset the answering interface.
					//   Also update the user info if we are on public view (to get the
					//   match percentage).
					//
					// if we don't see the profile questions app, but we are in the
					// native apps, that means we're answering a question.
					// * if we're on self view (i.e. if the `tuid` param is present),
					// trigger a phone command to let the apps know what we answered (the
					// app will then close the webview and navigate back).
					if (!store.questionsAppStandalone && profileQuestionsStore) {
						if (
							profileQuestionsStore.isSelfView &&
							profileQuestionsStore.nextQuestion &&
							profileQuestionsStore.nextQuestion.id === question.id
						) {
							dispatch(fetchNextQuestion());

							const questionCategories = getSelfviewQuestionCategories(res.question);

							if (
								profileQuestionsStore.activeFilter &&
								questionCategories.indexOf(profileQuestionsStore.activeFilter) >= 0
							) {
								dispatch(getQuestions({ resetPagination: true }));
							}
						} else {
							dispatch({ type: CLEAR_ANSWERING_QUESTION });

							dispatch({
								type: FETCH_QUESTION_SUCCESS,
								payload: {
									question: INITIAL_STATE.question,
								},
							});

							const userid = profileQuestionsStore[USER_TYPES.TARGET].userid;
							dispatch(getFilters(userid));

							if (!profileQuestionsStore.isSelfView) {
								dispatch(getUserInfo(userid, USER_TYPES.TARGET));
							}
						}
					} else if (
						isApp() &&
						UrlHelpers.getParam("tuid") &&
						window.App &&
						window.App.dataEvent &&
						typeof window.App.dataEvent === "function"
					) {
						// native app public view
						window.App.dataEvent("userQuestionsAnsweredEvent", {
							question: res.question,
						});
					} else if (
						// native app self view ask page
						(isApp() && !UrlHelpers.getParam("tuid")) ||

						// mobile web self view ask page
						(
							!isApp() &&
							process.env.PLATFORM === "mobile" &&
							window.location.pathname.indexOf("/questions/ask") >= 0
						)
					) {
						dispatch(fetchNextQuestion());

						// send a dataevent to trigger the app selfview main page to refresh
						// its next question
						if (window.App && window.App.dataEvent) {
							window.App.dataEvent("userAnsweredNextQuestion");
						}
					}
				} else {
					console.error(res);
					dispatch({
						type: SUBMIT_ANSWER_FAILURE,
						payload: {
							error: res.error && res.error.message ?
								res.error.message :
								defaultError,
						},
					});
				}
			})
			.catch((e) => {
				console.error(e);
				dispatch({
					type: SUBMIT_ANSWER_FAILURE,
					payload: {
						error: e.error && e.error.message ?
							e.error.message :
							defaultError,
					},
				});
			});
	};
}

export function submitBinaryQuestion(params) {
	return (dispatch, getStore) => {
		const store = getStore().questions;
		const { isProcessing } = store;

		// if we're already fetching, do nothing
		if (isProcessing) {
			return;
		}

		dispatch({ type: SUBMIT_BINARY_ANSWER });

		web.answerBinaryQuestion(params)
			.then(() => {
				dispatch({
					type: SUBMIT_BINARY_ANSWER_SUCCESS,
					payload: params,
				});

				dispatch(fetchNextQuestion());
			})
			.catch((e) => {
				dispatch({ type: SUBMIT_BINARY_ANSWER_FAILURE });
				dispatch({
					type: SUBMIT_ANSWER_FAILURE,
					payload: {
						error: e.error.message,
					},
				});
			});
	};
}

export function skipQuestion() {
	return (dispatch, getStore) => {
		const store = getStore().questions;
		const profileQuestionsStore = getStore().profileQuestions;
		const { question, isProcessing } = store;

		// if we're already fetching, do nothing
		if (isProcessing) {
			return;
		}

		dispatch({ type: SKIP_ANSWER });

		web.skipQuestion(question)
			.then(() => {
				dispatch({ type: SKIP_ANSWER_SUCCESS });
				dispatch(fetchNextQuestion());

				// if we are in the profile questions app context, update the question
				// list and sidebar counts
				if (profileQuestionsStore) {
					const userid = profileQuestionsStore[USER_TYPES.TARGET].userid;

					dispatch(getQuestions({ resetPagination: true }));
					dispatch(getFilters(userid));

					// send a dataevent to trigger the app selfview main page to refresh
					// its next question
					if (window.App && window.App.dataEvent) {
						window.App.dataEvent("userAnsweredNextQuestion");
					}
				}
			})
			.catch((e) => {
				console.error(e);

				dispatch({
					type: SKIP_ANSWER_FAILURE,
					payload: {
						error: e.error ? e.error.message : e.message,
					},
				});
			});
	};
}

export function selectAnswer(index) {
	return { type: SELECT_ANSWER, payload: { index } };
}

export function toggleAcceptedAnswer(index) {
	return { type: TOGGLE_ACCEPTED_ANSWER, payload: { index } };
}

export function setImportance(importance) {
	return {
		type: SET_IMPORTANCE,
		payload: {
			importance,
		},
	};
}

export function togglePrivacy() {
	return { type: TOGGLE_PRIVACY };
}

export function changeExplanation(explanation) {
	return { type: CHANGE_EXPLANATION, payload: { explanation } };
}

export function setGenre(genre) {
	return (dispatch, getStore) => {
		const store = getStore().questions;
		const { currentGenre } = store;

		// re-selecting the current genre de-selects them
		if (currentGenre === genre) {
			dispatch({ type: SET_GENRE, payload: { genre: null } });
		} else {
			dispatch({ type: SET_GENRE, payload: { genre } });
		}

		dispatch(fetchNextQuestion());
	};
}

export function setBinaryQuestionsMode(mode) {
	return {
		type: SET_BINARY_QUESTIONS_MODE,
		payload: {
			isBinaryMode: !!mode,
		},
	};
}



// WEBPACK FOOTER //
// ./src/ducks/questions.js